"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useGetAllAggregatedAttributes = void 0;
const react_1 = require("react");
const attributes_utils_1 = require("./attributes.utils");
const use_get_aggregated_text_attributes_hook_1 = require("./use-get-aggregated-text-attributes.hook");
const client_constants_1 = require("../../client.constants");
const measures_1 = require("../../measures");
const attributes_filter_utils_1 = require("../../../preview-products-widget/strategy-attributes/attributes-filter/attributes-filter.utils");
const feature_flags_1 = require("../../../../feature-flags");
const hooks_1 = require("../../../../hooks");
function useCombinedPagination(sources) {
    const hasNextPage = sources.some((s) => s.hasNextPage);
    const isLoadingMore = sources.some((s) => s.isLoadingMore);
    const onLoadMore = (0, react_1.useCallback)(() => __awaiter(this, void 0, void 0, function* () {
        if (!hasNextPage)
            return;
        const promises = sources
            .filter((s) => s.hasNextPage)
            .map((s) => s.onNextPage());
        yield Promise.all(promises);
    }), [hasNextPage, sources]);
    return { hasNextPage, isLoadingMore, onLoadMore };
}
/**
 *
 * @returns aggregated "In Strategy" text and numeric attributes for the selected node and all children
 */
const useGetAllAggregatedAttributes = ({ catalogId, productTreeNodeId, sortingItem, options, pageSize = client_constants_1.PAGE_SIZE_LARGE, productLabelFilter, productValueFilter, productMeasureFilter, }) => {
    const { featureFlagsService } = (0, hooks_1.useFeatureFlagsService)();
    const { data: productMeasuresData, isLoading: productMeasuresLoading, isRefetching: productMeasuresRefetching, error: productMeasuresError, pagination: { isLoadingMore: productMeasuresMoreItemsLoading, onNextPage: fetchMoreProductMeasures, hasNextPage: productMeasuresHasNextPage, }, } = (0, measures_1.useGetAggregatedProductMeasures)({
        catalogId,
        productTreeNodeId,
        sortingItem,
        pageSize,
        filter: productMeasureFilter,
        options: Object.assign(Object.assign({}, options), { skip: !featureFlagsService.isFeatureEnabled(feature_flags_1.FeatureFlag.Measures) || (options === null || options === void 0 ? void 0 : options.skip) }),
    });
    const { data: productAttributesData, isLoading: productAttributesLoading, isRefetching: productAttributesRefetching, error: productAttributesError, pagination: { isLoadingMore: productAttributesMoreItemsLoading, onNextPage: fetchMoreProductAttributes, hasNextPage: productAttributesHasNextPage, }, } = (0, use_get_aggregated_text_attributes_hook_1.useGetAggregatedTextAttributes)({
        catalogId,
        productTreeNodeId,
        sortingItem,
        pageSize,
        options,
        isRequirement: true,
        filter: productLabelFilter,
    });
    const { data: productAttributesByValueData, isLoading: productAttributesByValueLoading, isRefetching: productAttributesByValueRefetching, error: productAttributesByValueError, pagination: { isLoadingMore: productAttributesByValueMoreItemsLoading, onNextPage: fetchMoreProductAttributesByValue, hasNextPage: productAttributesByValueHasNextPage, }, } = (0, use_get_aggregated_text_attributes_hook_1.useGetAggregatedTextAttributes)({
        catalogId,
        productTreeNodeId,
        sortingItem,
        pageSize,
        options: Object.assign(Object.assign({}, options), { skip: !productValueFilter || (options === null || options === void 0 ? void 0 : options.skip) }),
        isRequirement: true,
        filter: productValueFilter,
    });
    const [pageNumber, setPageNumber] = (0, react_1.useState)(1);
    const paginationSources = (0, react_1.useMemo)(() => [
        {
            hasNextPage: productAttributesHasNextPage,
            onNextPage: fetchMoreProductAttributes,
            isLoadingMore: productAttributesMoreItemsLoading,
        },
        {
            hasNextPage: productAttributesByValueHasNextPage,
            onNextPage: fetchMoreProductAttributesByValue,
            isLoadingMore: productAttributesByValueMoreItemsLoading,
        },
        {
            hasNextPage: productMeasuresHasNextPage,
            onNextPage: fetchMoreProductMeasures,
            isLoadingMore: productMeasuresMoreItemsLoading,
        },
    ], [
        productAttributesHasNextPage, fetchMoreProductAttributes, productAttributesMoreItemsLoading,
        productAttributesByValueHasNextPage, fetchMoreProductAttributesByValue, productAttributesByValueMoreItemsLoading,
        productMeasuresHasNextPage, fetchMoreProductMeasures, productMeasuresMoreItemsLoading,
    ]);
    const { hasNextPage, isLoadingMore, onLoadMore: loadMore } = useCombinedPagination(paginationSources);
    (0, react_1.useEffect)(() => {
        setPageNumber(1);
    }, [productTreeNodeId, productLabelFilter, productValueFilter, productMeasureFilter]);
    const onLoadMore = (0, react_1.useCallback)(() => __awaiter(void 0, void 0, void 0, function* () {
        yield loadMore();
        setPageNumber((prev) => prev + 1);
    }), [loadMore]);
    const attributes = (0, react_1.useMemo)(() => {
        if (!productAttributesData && !productMeasuresData && !productAttributesByValueData) {
            return [];
        }
        const mergedAttributes = (0, attributes_filter_utils_1.mergeAndDeduplicateAttributes)(productAttributesData, productAttributesByValueData, productMeasuresData);
        const sortFunction = attributes_utils_1.sortingItemToLocalSortMap[sortingItem];
        const sortedAttributes = sortFunction(mergedAttributes, attributes_utils_1.sortingItemToPropName[sortingItem]);
        if (!hasNextPage) {
            return sortedAttributes;
        }
        const currentLastItemNumber = pageSize * pageNumber;
        return sortedAttributes.slice(0, currentLastItemNumber);
    }, [hasNextPage, pageNumber, pageSize, productAttributesData, productAttributesByValueData, productMeasuresData, sortingItem]);
    const isValueSearchLoading = !!productValueFilter
        && productAttributesByValueLoading && !productAttributesLoading;
    return {
        isLoading: productAttributesLoading || productMeasuresLoading || productAttributesByValueLoading,
        isRefetching: productAttributesRefetching || productMeasuresRefetching || productAttributesByValueRefetching,
        error: productAttributesError || productMeasuresError || productAttributesByValueError,
        data: attributes,
        pagination: {
            isLoadingMore: isLoadingMore || isValueSearchLoading,
            onNextPage: onLoadMore,
            hasNextPage,
        },
    };
};
exports.useGetAllAggregatedAttributes = useGetAllAggregatedAttributes;
exports.default = exports.useGetAllAggregatedAttributes;
